// src/store/slices/Contact/contactSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { contactAPI, ContactInfo, FeedbackRequest } from '../../../api/contactApi';

// State interface
interface ContactState {
    contactInfo: ContactInfo | null;
    loading: boolean;
    error: string | null;
    feedbackStatus: {
        loading: boolean;
        success: boolean;
        error: string | null;
    };
}

// Initial state
const initialState: ContactState = {
    contactInfo: null,
    loading: false,
    error: null,
    feedbackStatus: {
        loading: false,
        success: false,
        error: null,
    },
};

// Async thunks
export const fetchContactInfo = createAsyncThunk(
    'contact/fetchContactInfo',
    async (_, { rejectWithValue }) => {
        try {
            const data = await contactAPI.getContactInfo();
            return data[0] || null;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Məlumat yüklənərkən xəta baş verdi');
        }
    }
);

export const submitFeedback = createAsyncThunk(
    'contact/submitFeedback',
    async (feedbackData: FeedbackRequest, { rejectWithValue }) => {
        try {
            const response = await contactAPI.submitFeedback(feedbackData);
            return response;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Feedback göndərilərkən xəta baş verdi');
        }
    }
);

// Slice
const contactSlice = createSlice({
    name: 'contact',
    initialState,
    reducers: {
        resetFeedbackStatus: (state) => {
            state.feedbackStatus = {
                loading: false,
                success: false,
                error: null,
            };
        },
        clearError: (state) => {
            state.error = null;
            state.feedbackStatus.error = null;
        },
    },
    extraReducers: (builder) => {
        // Fetch Contact Info
        builder
            .addCase(fetchContactInfo.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchContactInfo.fulfilled, (state, action: PayloadAction<ContactInfo | null>) => {
                state.loading = false;
                state.contactInfo = action.payload;
                state.error = null;
            })
            .addCase(fetchContactInfo.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });

        // Submit Feedback
        builder
            .addCase(submitFeedback.pending, (state) => {
                state.feedbackStatus.loading = true;
                state.feedbackStatus.success = false;
                state.feedbackStatus.error = null;
            })
            .addCase(submitFeedback.fulfilled, (state) => {
                state.feedbackStatus.loading = false;
                state.feedbackStatus.success = true;
                state.feedbackStatus.error = null;
            })
            .addCase(submitFeedback.rejected, (state, action) => {
                state.feedbackStatus.loading = false;
                state.feedbackStatus.success = false;
                state.feedbackStatus.error = action.payload as string;
            });
    },
});

export const { resetFeedbackStatus, clearError } = contactSlice.actions;
export default contactSlice.reducer;